import { z } from 'zod/v4'
import type { Agent } from 'skedyul-db/browser'
import { ContextType } from '@context/session'
import { OptionalMemberContextType } from '@context/session'
import type { AgentContext } from 'skedyul'

// Re-export the type from skedyul
export type { AgentContext }

// Local schema definition to avoid cross-package Zod type inference issues in .d.ts generation.
// This schema is structurally identical to AgentContextSchema in skedyul-node.
// Named differently to avoid conflict with AgentContextSchema in flows/conversationEntityContext.ts
const AgentSenderContextSchemaLocal = z.object({
  kind: z.enum(['contact', 'member']),
  displayName: z.string().optional(),
  role: z.string().optional(),
  permissions: z.array(z.string()).optional(),
  contact: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
      subscription: z
        .object({
          identifierValue: z.string(),
          channelHandle: z.string().optional(),
        })
        .optional(),
      associations: z
        .record(
          z.string(),
          z.object({
            id: z.string().optional(),
            data: z.record(z.string(), z.unknown()),
          }),
        )
        .optional(),
    })
    .optional(),
})

const AgentThreadContextSchemaLocal = z.object({
  handle: z.string(),
  model: z.string(),
  data: z.record(z.string(), z.unknown()),
})

const AgentRunContextSchema = z.object({
  sender: AgentSenderContextSchemaLocal,
  contexts: z.array(AgentThreadContextSchemaLocal).optional(),
})

/** Agent run / sandbox context schema (mirrors skedyul AgentContextSchema) */
export { AgentRunContextSchema }

// ─────────────────────────────────────────────────────────────────────────────
// Agent run trace & events
// ─────────────────────────────────────────────────────────────────────────────

export const AgentRunEventKindSchema = z.enum([
  'THOUGHT',
  'TOOL_CALL_STARTED',
  'TOOL_CALL_COMPLETED',
  'TOOL_APPROVAL_REQUIRED',
  'MESSAGE_DELTA',
  'MESSAGE_FINAL',
  'CONTEXT_UPDATE',
])

export const AgentRunEventMessageRoleSchema = z.enum([
  'user',
  'assistant',
  'system',
])

export const AgentRunEventSchema = z.object({
  /**
   * Monotonically increasing index for this event within an AgentRun.
   * Useful for ordering when stored in JSON.
   */
  index: z.number().int().nonnegative(),
  kind: AgentRunEventKindSchema,
  /**
   * Name of the agent producing this event (for multi-stage pipelines).
   */
  agentName: z.string().optional(),
  /**
   * Whether this event is from a child agent invocation (for multi-stage pipelines).
   * Used by CLI to indent child agent output for visual hierarchy.
   */
  isChildAgent: z.boolean().optional(),
  /**
   * Full output from an agent stage (for debugging multi-stage pipelines).
   * Unlike thought content, this is not truncated in CLI display.
   */
  agentOutput: z.string().optional(),
  /**
   * Timestamp (epoch ms) when this event/step started.
   */
  startedAt: z.number().optional(),
  /**
   * Duration of this step in milliseconds.
   */
  durationMs: z.number().optional(),
  /**
   * Optional message payload when the event represents agent or user text.
   */
  message: z
    .object({
      role: AgentRunEventMessageRoleSchema,
      content: z.string(),
      /**
       * Whether this message represents the final assistant output
       * for the current turn.
       */
      isFinal: z.boolean().optional(),
    })
    .optional(),
  /**
   * Optional tool call payload when the event represents a tool invocation.
   */
  toolCall: z
    .object({
      name: z.string(),
      /** User-friendly display name for the tool */
      displayName: z.string().optional(),
      status: z.enum(['STARTED', 'SUCCEEDED', 'FAILED']).optional(),
      args: z.record(z.string(), z.unknown()).optional(),
      resultSummary: z.string().optional(),
      error: z.string().optional(),
      /**
       * Rich data blocks from tool results to be forwarded to the UI.
       * Validated lazily to allow schema to be defined after this point.
       */
      data: z.array(z.lazy(() => DataBlockSchema)).optional(),
      /**
       * Timestamp (epoch ms) when the tool call started.
       */
      startedAt: z.number().optional(),
      /**
       * Duration of the tool call in milliseconds.
       */
      durationMs: z.number().optional(),
      /**
       * Whether this tool was auto-approved in sandbox mode.
       */
      autoApproved: z.boolean().optional(),
    })
    .optional(),
  /**
   * Pending approval request for a tool that requires user confirmation.
   * Sent with TOOL_APPROVAL_REQUIRED events.
   */
  pendingApproval: z
    .object({
      toolCallId: z.string(),
      toolName: z.string(),
      displayName: z.string(),
      args: z.record(z.string(), z.unknown()),
    })
    .optional(),
  /**
   * Context chips representing entities touched in the conversation.
   * Sent with CONTEXT_UPDATE events.
   */
  context: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        hint: z.string().optional(),
      }),
    )
    .optional(),
  /**
   * Free-form metadata for future extension – must remain JSON-serializable.
   */
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const AgentRunTraceSchema = z.object({
  events: z.array(AgentRunEventSchema).default([]),
})

export type AgentRunEvent = z.infer<typeof AgentRunEventSchema>
export type AgentRunTrace = z.infer<typeof AgentRunTraceSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Agent configuration & tool call responses
// ─────────────────────────────────────────────────────────────────────────────

export const ToolCallResponseSchema = z.object({
  id: z.string(),
  toolName: z.string(),
  toolDescription: z.string(),
  startedAt: z
    .date()
    .optional()
    .nullable()
    .transform((date) => date?.toISOString())
    .or(z.string()),
  completedAt: z
    .date()
    .optional()
    .nullable()
    .transform((date) => date?.toISOString())
    .or(z.string()),
  createdAt: z
    .date()
    .transform((date) => date?.toISOString())
    .or(z.string()),
  updatedAt: z
    .date()
    .optional()
    .nullable()
    .transform((date) => date?.toISOString())
    .or(z.string()),
  error: z.string().optional(),
  args: z.record(z.string(), z.any()),
  response: z.record(z.string(), z.any()).nullable(),
})

export const CreateAgentSchema = z.object({
  name: z.string(),
  handle: z.string().optional(),
  description: z.string(),
  system: z.string(),
  llmModelId: z.string(),
})

export type ToolCallResponse = z.infer<typeof ToolCallResponseSchema>

export type CreateAgentInput = z.infer<typeof CreateAgentSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Default agent definitions
// ─────────────────────────────────────────────────────────────────────────────

export const DefaultAgentDefinitionSchema = z.object({
  /**
   * Unique key for this default agent (e.g. "crm-assistant", "ops-copilot")
   */
  key: z.string(),
  /**
   * Display name for the agent
   */
  name: z.string(),
  /**
   * Description of what this agent does
   */
  description: z.string(),
  /**
   * System prompt for the agent
   */
  system: z.string(),
  /**
   * LLM model ID to use (references LlmModel.modelId)
   */
  llmModelId: z.string(),
  /**
   * Whether this agent should be created by default for new workplaces
   */
  isDefault: z.boolean().default(true),
})

export const DefaultAgentsConfigSchema = z.object({
  agents: z.array(DefaultAgentDefinitionSchema),
})

export type DefaultAgentDefinition = z.infer<
  typeof DefaultAgentDefinitionSchema
>
export type DefaultAgentsConfig = z.infer<typeof DefaultAgentsConfigSchema>

// ─────────────────────────────────────────────────────────────────────────────
// App agent definitions (from skedyul.config.ts)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Schema for app-provided agent definitions.
 * These are synced from Executable.config.agents during provisioning.
 */
export const AppAgentDefinitionSchema = z.object({
  /** Unique identifier within the app (used for upserts) */
  handle: z
    .string()
    .regex(
      /^[a-z0-9_-]+$/,
      'Handle must be lowercase alphanumeric with dashes/underscores',
    ),
  /** Display name */
  name: z.string().min(1),
  /** Description of what the agent does */
  description: z.string(),
  /** Copy for a public website. Not the system prompt or persona. */
  public: z
    .object({
      summary: z.string().optional(),
      details: z.string().optional(),
    })
    .optional(),
  /** System prompt (static, no templating) */
  system: z.string(),
  /** Tool names to bind (can be empty for orchestrator agents) */
  tools: z.array(z.string()),
  /** Optional LLM model override */
  llmModelId: z.string().optional(),
  /** Parent agent that can call this agent ('composer' or another agent handle) */
  parentAgent: z.string().optional(),
})

export type AppAgentDefinition = z.infer<typeof AppAgentDefinitionSchema>

export type CreateAgentType = z.infer<typeof CreateAgentSchema> &
  OptionalMemberContextType

/**
 * Root-level persona configuration for agents.
 * Defines voice and formatting rules that apply to all message outputs.
 */
export const PersonaConfigSchema = z.object({
  /** Display name for the persona (e.g., "Brett") */
  name: z.string(),
  /** Voice configuration with style and format rules */
  voice: z.object({
    /** Style guidelines as a single string (voice, tone, personality) */
    style: z.string(),
    /** Formatting constraints */
    format: z
      .object({
        /** Maximum character count for messages */
        maxChars: z.number().optional(),
        /** Whether to avoid emojis */
        noEmojis: z.boolean().optional(),
        /** Whether to avoid hyphens */
        noHyphens: z.boolean().optional(),
        /** Whether to avoid bullet points */
        noBulletPoints: z.boolean().optional(),
        /** Maximum questions per message */
        maxQuestionsPerMessage: z.number().optional(),
        /** Whether to avoid sign-offs like "Cheers" or "Thanks" */
        noSignOffs: z.boolean().optional(),
      })
      .optional(),
  }),
})

export type PersonaConfig = z.infer<typeof PersonaConfigSchema>

export const UpdateAgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  system: z.string(),
  llmModelId: z.string(),
  persona: PersonaConfigSchema.nullable().optional(),
})

export type UpdateAgentInput = z.infer<typeof UpdateAgentSchema>

export type UpdateAgentType = z.infer<typeof UpdateAgentSchema> & ContextType

export type CreateAgentReturnType = {
  agent: Agent | null
  errors: Record<string, string>
}

export type UpdateAgentReturnType = {
  agent: Agent | null
  errors: Record<string, string>
}

export type ChatMessage = {
  role: 'user' | 'assistant' | 'system'
  createdAt: string
  content: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent stream flow input/output
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Overrides for agent configuration during a run.
 * Useful for testing different system prompts, models, or tool sets.
 */
export const AgentOverridesSchema = z.object({
  /**
   * Override the agent's system prompt.
   */
  systemPrompt: z.string().optional(),
  /**
   * Override the agent's LLM model ID (e.g. "gpt-4o", "gpt-4o-mini").
   */
  llmModelId: z.string().optional(),
  /**
   * Override which tool IDs should be available. If provided,
   * only these tools will be used instead of the agent's bindings.
   */
  toolIds: z.array(z.string()).optional(),
})

export type AgentOverrides = z.infer<typeof AgentOverridesSchema>

/**
 * Schema for linked instance context (from threads or CLI).
 * Represents CRM records linked to the conversation.
 */
export const LinkedInstanceSchema = z.object({
  /** CRM instance ID */
  instanceId: z.string(),
  /** CRM model ID */
  modelId: z.string(),
  /** Human-readable model label (e.g. "Customer", "Prospect") */
  modelLabel: z.string().nullable(),
  /** Human-readable instance label (e.g. "John Doe") */
  label: z.string(),
  /** Role of this instance in the conversation */
  role: z.enum(['PRIMARY', 'SECONDARY', 'INFERRED']).optional(),
  /** Reason for linking (e.g. "matched by phone number") */
  reason: z.string().optional(),
})

/**
 * Schema for entity context (for CLI and direct API calls).
 * Includes full entity data for context injection.
 */
export const EntityContextSchema = z.object({
  /** CRM instance ID */
  instanceId: z.string(),
  /** CRM model ID or handle */
  modelId: z.string(),
  /** Human-readable model label */
  modelLabel: z.string().nullable(),
  /** Human-readable instance label */
  label: z.string(),
  /** Full entity data (optional, for rich context injection) */
  data: z.record(z.string(), z.unknown()).optional(),
})

/**
 * Schema for thread context passed to agent execution.
 */
export const ThreadContextSchema = z.object({
  /** Linked CRM instances */
  linkedInstances: z.array(LinkedInstanceSchema).optional(),
  /** Routing participant ID for role assignment */
  routingParticipantId: z.string().optional(),
})

/**
 * Schema for chat history messages.
 */
export const ChatHistoryMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
})

/**
 * Schema for tool history entries.
 * Tracks tool calls with their arguments and results for conversation memory.
 * Used to provide the LLM with context about previous tool executions.
 */
export const ToolHistoryEntrySchema = z.object({
  /** Unique identifier for this tool call */
  toolCallId: z.string(),
  /** Name of the tool that was called */
  toolName: z.string(),
  /** The AI SDK handle name (e.g., 'load_skill' for 'system:skill:load'). Required for tool history replay. */
  handle: z.string().optional(),
  /** Arguments passed to the tool */
  args: z.record(z.string(), z.unknown()),
  /** Result returned by the tool */
  result: z.unknown(),
  /** Timestamp when the tool was called (epoch ms) */
  timestamp: z.number(),
  /** Provider-specific options (e.g., Gemini's thought_signature for tool call replay) */
  providerOptions: z.record(z.string(), z.unknown()).optional(),
})

export type LinkedInstance = z.infer<typeof LinkedInstanceSchema>
export type EntityContext = z.infer<typeof EntityContextSchema>
export type ThreadContext = z.infer<typeof ThreadContextSchema>
export type ChatHistoryMessage = z.infer<typeof ChatHistoryMessageSchema>
export type ToolHistoryEntry = z.infer<typeof ToolHistoryEntrySchema>

export const RunAgentStreamInputSchema = z.object({
  workplaceId: z.string(),
  /**
   * Optional identifier for the member initiating the run.
   * Currently used only for future auditing/permissions.
   */
  memberId: z.string().optional(),
  /**
   * Optional chat id if this run is associated with a Chat.
   */
  chatId: z.string().optional(),
  /**
   * The latest user message.
   */
  prompt: z.string(),
  /**
   * Optional agent ID to use. If not provided, uses the default workplace agent.
   */
  agentId: z.string().optional(),
  /**
   * Optional agent version ID to pin to a specific version.
   * If not provided, uses A/B testing logic to select a version.
   */
  agentVersionId: z.string().optional(),
  /**
   * Optional overrides for testing different configurations.
   */
  agentOverrides: AgentOverridesSchema.optional(),

  // ─────────────────────────────────────────────────────────────────────────────
  // Unified context fields (for threads, CLI, and direct API calls)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Optional thread ID if this run is associated with a Thread.
   * Used for tool execution context and message routing.
   */
  threadId: z.string().optional(),
  /**
   * Optional thread context with linked instances and routing info.
   * Used by thread-based execution for context injection.
   */
  threadContext: ThreadContextSchema.optional(),
  /**
   * Optional entity context for CLI and direct API calls.
   * Includes full entity data for rich context injection.
   */
  entityContext: z.array(EntityContextSchema).optional(),
  /**
   * Optional chat history override.
   * If provided, uses this instead of loading from database.
   */
  chatHistory: z.array(ChatHistoryMessageSchema).optional(),
  /**
   * Optional tool history from previous turns.
   * Contains tool calls with their arguments and results for conversation memory.
   * Used to provide the LLM with context about previous tool executions.
   */
  toolHistory: z.array(ToolHistoryEntrySchema).optional(),
  /**
   * Optional workplace subdomain for S3 loading and other operations.
   */
  subdomain: z.string().optional(),
  /**
   * Optional workflow inputs for multi-stage agents.
   * These are passed directly to the workflow and available via {{ input.key }}.
   */
  workflowInputs: z.record(z.string(), z.string()).optional(),
  /**
   * Enable sandbox mode - no real thread/message persistence.
   * Used for testing agents without side effects.
   */
  sandbox: z.boolean().optional(),
  /**
   * Agent context - provides sender and CRM data.
   * Used for both sandbox testing and production threads.
   */
  context: AgentRunContextSchema.optional(),
  /** @deprecated Use 'context' instead */
  mockContext: AgentRunContextSchema.optional(),
  /**
   * Selected sandbox scenario id from agent YAML (sandbox.scenarios).
   */
  sandboxScenarioId: z.string().optional(),
  /**
   * Current time in ISO 8601 format.
   * Used for time-aware agent responses when combined with agent's timezone config.
   */
  currentTime: z.string().optional(),
  /**
   * Whether to return a streaming response or run to completion.
   * - true (default): Returns eventStream async generator for real-time streaming
   * - false: Runs agent to completion and returns final result directly
   */
  streaming: z.boolean().optional(),
})

/**
 * Extended input type that includes runtime-only fields not in the schema.
 * The signal field cannot be serialized via Zod, so we extend the type.
 */
export type RunAgentStreamInput = z.infer<typeof RunAgentStreamInputSchema> & {
  /**
   * Optional AbortSignal to detect client disconnection.
   * When aborted, the streaming loop will stop polling.
   */
  signal?: AbortSignal
}

/**
 * Result type for non-streaming agent execution.
 * Returned when streaming=false is passed to runAgentStreamFlow.
 */
export type RunAgentResult = {
  /** The agent run ID for tracking */
  agentRunId: string
  /** The final message from the agent */
  message: string
  /** Tool calls made during execution */
  toolCalls: Array<{
    toolCallId: string
    toolName: string
    displayName?: string
    args: Record<string, unknown>
    result: unknown
  }>
  /** Reasoning/thoughts from the agent */
  reasoning: string[]
  /** Updated context (reflects CRM updates from tool calls) */
  updatedContext?: unknown
}

export type AgentResponse = {
  message: string
  toolCalls: Array<{
    name: string
    input: string
    workflowRunId?: string
  }>
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent conversation message content
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Schema for a single thought in an agent's reasoning process.
 */
export const AgentThoughtSchema = z.object({
  /** The thought content/reasoning */
  content: z.string(),
  /** Duration of the thinking phase in milliseconds */
  durationMs: z.number().optional(),
})

/**
 * Schema for the JSON content field of AgentConversationMessage.
 * Supports structured storage of thoughts separate from the main message text.
 */
export const AgentMessageContentSchema = z.object({
  /** The main message text (the actual response) */
  text: z.string(),
  /** Array of thoughts/reasoning phases (shown as collapsible "Thought for Xs") */
  thoughts: z.array(AgentThoughtSchema).optional(),
  /** Whether this message represents an error */
  error: z.boolean().optional(),
  /**
   * Rich data blocks from tool results to display in the UI.
   * Validated lazily to allow schema to be defined after this point.
   */
  data: z.array(z.lazy(() => DataBlockSchema)).optional(),
})

export type AgentThought = z.infer<typeof AgentThoughtSchema>
export type AgentMessageContent = z.infer<typeof AgentMessageContentSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Agent input definitions (for collecting user inputs before agent execution)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Option for select-type agent inputs.
 */
export const AgentInputOptionSchema = z.object({
  /** Value to store when selected */
  value: z.string(),
  /** Display label for the option */
  label: z.string(),
})

/**
 * Schema for a single agent input field.
 * Defines what inputs the agent needs before it can run.
 */
export const AgentInputSchema = z.object({
  /** Unique key for this input (used in Thread.inputs) */
  key: z.string(),
  /** Display label for the input field */
  label: z.string().optional(),
  /** Help text/description for the input */
  description: z.string().optional(),
  /** Whether this input is required */
  required: z.boolean().optional().default(false),
  /** Input type for rendering appropriate control */
  type: z
    .enum(['text', 'number', 'select', 'textarea'])
    .optional()
    .default('text'),
  /** Options for select-type inputs */
  options: z.array(AgentInputOptionSchema).optional(),
  /** Placeholder text for the input */
  placeholder: z.string().optional(),
})

export type AgentInputOption = z.infer<typeof AgentInputOptionSchema>
export type AgentInput = z.infer<typeof AgentInputSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Rich data blocks (for tool results forwarded to UI)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Link structure for navigating to CRM views or external URLs.
 */
export const DataBlockLinkSchema = z.object({
  /** App CRM entity handle (e.g. "class", "booking") for UI map gating */
  entityHandle: z.string().optional(),
  /** Model handle singular (e.g. "customer") */
  modelHandle: z.string().optional(),
  /** Model handle plural for URL routing (e.g. "customers") */
  modelHandlePlural: z.string().optional(),
  /** Model display label (e.g. "Customer") for link text */
  modelLabel: z.string().optional(),
  /** Record ID for CRM navigation */
  recordId: z.string().optional(),
  /** Optional filters for list views (JSON-serializable) */
  filters: z.record(z.string(), z.unknown()).optional(),
  /** Direct URL if not using CRM routing */
  url: z.string().optional(),
})

/**
 * Column definition for spreadsheet blocks.
 * Matches the shape expected by the Spreadsheet UI component.
 */
export const SpreadsheetColumnSchema = z.object({
  /** Unique column identifier (field handle) */
  id: z.string(),
  /** Display label for the column header */
  label: z.string(),
  /** Data type hint for rendering (text, number, date, etc.) */
  type: z.string().optional(),
})

/**
 * Spreadsheet block - tabular data with preview rows.
 * Max 5 preview rows, with totalRows for "showing X of Y".
 * Shape is compatible with the Spreadsheet UI component.
 */
export const SpreadsheetBlockSchema = z.object({
  type: z.literal('spreadsheet'),
  /** Display title for the spreadsheet */
  title: z.string(),
  /** Column definitions matching Spreadsheet component's ColumnConfig */
  columns: z.array(SpreadsheetColumnSchema),
  /** Data rows - array of objects with id and field values */
  data: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
  /** Total row count for "showing X of Y" display */
  totalRows: z.number().int().nonnegative(),
  /** Link for "view more" navigation */
  link: DataBlockLinkSchema.optional(),
  /** When set to groups, chat copy says "groups" instead of records */
  rowNoun: z.enum(['records', 'groups']).optional(),
  /** Underlying matching record count for aggregate "View N matching X" */
  matchingCount: z.number().int().nonnegative().optional(),
})

/**
 * Avatar configuration for profile blocks.
 */
export const ProfileAvatarSchema = z.object({
  /** Initials to display (e.g. "JD" for John Doe) */
  initials: z.string().optional(),
  /** Image URL for avatar */
  imageUrl: z.string().optional(),
})

/**
 * Field entry for profile blocks.
 */
export const ProfileFieldSchema = z.object({
  /** Display label (e.g. "Email", "Phone") */
  label: z.string(),
  /** Field value */
  value: z.string(),
  /** Optional CRM navigation for this field value */
  link: DataBlockLinkSchema.optional(),
})

/**
 * Profile block - generic CRM record with key fields.
 * Max 6 preview fields recommended.
 */
export const ProfileBlockSchema = z.object({
  type: z.literal('profile'),
  /** Display title (e.g. customer name) */
  title: z.string(),
  /** Subtitle (e.g. role, status) */
  subtitle: z.string().optional(),
  /** Avatar configuration */
  avatar: ProfileAvatarSchema.optional(),
  /** Preview fields (max 6 recommended) */
  fields: z.array(ProfileFieldSchema),
  /** Link for "view more" navigation */
  link: DataBlockLinkSchema.optional(),
})

/**
 * Error structure for field change validation errors.
 */
export const FieldChangeErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
})

/**
 * Single field change entry showing old → new value transition.
 */
export const FieldChangeItemSchema = z.object({
  /** Display label for the field (e.g. "Status", "Email") */
  label: z.string(),
  /** Previous value (null if field was empty) */
  oldValue: z.string().nullable(),
  /** New value (null if field is being cleared) */
  newValue: z.string().nullable(),
  /** Optional validation error for this field */
  error: FieldChangeErrorSchema.optional(),
})

/**
 * Field changes block - shows old → new value transitions for CRM updates.
 * Uses FieldChange component styling to display changes clearly.
 */
export const FieldChangesBlockSchema = z.object({
  type: z.literal('fieldChanges'),
  /** Display title (e.g. "Updated John Doe") */
  title: z.string(),
  /** Subtitle (e.g. model label or additional context) */
  subtitle: z.string().optional(),
  /** Array of field changes to display */
  changes: z.array(FieldChangeItemSchema),
  /** Link for "view record" navigation */
  link: DataBlockLinkSchema.optional(),
})

/**
 * DateTime block - shows a calendar event/appointment with status.
 * Useful for booking confirmations, scheduled meetings, etc.
 */
export const DateTimeBlockSchema = z.object({
  type: z.literal('dateTime'),
  /** Display title (e.g. "Booking Confirmed") */
  title: z.string(),
  /** Subtitle (e.g. "Max - Vaccination") */
  subtitle: z.string().optional(),
  /** ISO 8601 datetime string */
  datetime: z.string(),
  /** Timezone (e.g. "Australia/Melbourne") */
  timezone: z.string().optional(),
  /** Duration in minutes */
  duration: z.number().optional(),
  /** Location or calendar name */
  location: z.string().optional(),
  /** Status indicator for the event */
  status: z.enum(['confirmed', 'pending', 'cancelled']).optional(),
  /** Icon to display */
  icon: z.enum(['calendar', 'clock', 'check']).optional(),
  /** Link for navigation */
  link: DataBlockLinkSchema.optional(),
})

/**
 * Discriminated union of all data block types.
 * Tools can return these in their results to be forwarded to the UI.
 */
export const DataBlockSchema = z.discriminatedUnion('type', [
  SpreadsheetBlockSchema,
  ProfileBlockSchema,
  FieldChangesBlockSchema,
  DateTimeBlockSchema,
])

export type DataBlockLink = z.infer<typeof DataBlockLinkSchema>
export type SpreadsheetColumn = z.infer<typeof SpreadsheetColumnSchema>
export type SpreadsheetBlock = z.infer<typeof SpreadsheetBlockSchema>
export type ProfileAvatar = z.infer<typeof ProfileAvatarSchema>
export type ProfileField = z.infer<typeof ProfileFieldSchema>
export type ProfileBlock = z.infer<typeof ProfileBlockSchema>
export type FieldChangeError = z.infer<typeof FieldChangeErrorSchema>
export type FieldChangeItem = z.infer<typeof FieldChangeItemSchema>
export type FieldChangesBlock = z.infer<typeof FieldChangesBlockSchema>
export type DateTimeBlock = z.infer<typeof DateTimeBlockSchema>
export type DataBlock = z.infer<typeof DataBlockSchema>

// ─────────────────────────────────────────────────────────────────────────────
// MCP JSON-RPC & tools
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Execution scope for tools.
 * - `installation`: Tool requires appInstallationId, receives sk_wkp_ token (default).
 * - `app_version`: Tool runs without appInstallationId, receives sk_prv_ token.
 *   Used for developer/admin tools that discover installations from records.
 */
export const ExecutionScopeSchema = z.enum(['installation', 'app_version'])
export type ExecutionScope = z.infer<typeof ExecutionScopeSchema>

export const McpToolSchema = z.object({
  name: z.string(),
  displayName: z.string().optional(),
  description: z.string().optional(),
  inputSchema: z.record(z.string(), z.unknown()).optional(),
  outputSchema: z.record(z.string(), z.unknown()).optional(),
  /** Timeout in milliseconds for this tool. Defaults to 10000 (10 seconds) if not specified. */
  timeout: z.number().optional(),
  /** Maximum retry attempts. Defaults to 1 (no retries) if not specified. */
  retries: z.number().optional(),
  /** Execution scope: 'installation' (default) or 'app_version' for developer tools. */
  executionScope: ExecutionScopeSchema.optional(),
  /** Rate-limit queue touch points for orchestration admission probes */
  queueTouchPoints: z
    .array(
      z.object({
        queue: z.string().min(1),
        estimatedCalls: z.number().int().positive(),
        subKeyFromArg: z.string().optional(),
        mutexOnly: z.boolean().optional(),
      }),
    )
    .optional(),
})

export const McpWebhookSchema = z.object({
  name: z.string(),
  displayName: z.string().optional(),
  description: z.string().optional(),
  /** HTTP methods this webhook accepts */
  methods: z
    .array(z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']))
    .optional()
    .default(['POST']),
  /** Invocation type: WEBHOOK (fire-and-forget) or CALLBACK (waits for response) */
  type: z.enum(['WEBHOOK', 'CALLBACK']).optional().default('WEBHOOK'),
})

export const McpCronSchema = z.object({
  name: z.string(),
  label: z.string().optional(),
  description: z.string().optional(),
  /** Cron schedule expression (e.g. "0,15,30,45 * * * *") */
  schedule: z.string(),
  /** Timezone for the schedule (defaults to "UTC") */
  timezone: z.string().optional().default('UTC'),
  /** Whether the cron is enabled (defaults to true) */
  enabled: z.boolean().optional().default(true),
})

export const McpListToolsResultSchema = z.object({
  tools: z.array(McpToolSchema),
})

export const McpJsonRpcErrorSchema = z.object({
  code: z.number(),
  message: z.string(),
})

export const McpJsonRpcResponseSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: z.union([z.string(), z.number(), z.null()]),
  result: McpListToolsResultSchema.optional(),
  error: McpJsonRpcErrorSchema.optional(),
})

export type McpToolDescription = z.infer<typeof McpToolSchema>
export type McpWebhookDescription = z.infer<typeof McpWebhookSchema>
export type McpCronDescription = z.infer<typeof McpCronSchema>
export type McpListToolsResult = z.infer<typeof McpListToolsResultSchema>
export type McpJsonRpcResponse = z.infer<typeof McpJsonRpcResponseSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Workplace agent definitions (intent → tool mapping)
// ─────────────────────────────────────────────────────────────────────────────

export const WorkplaceIntentDefinitionSchema = z.object({
  /**
   * Stable identifier for the intent, e.g. "calendar.list" or "appointment.book".
   */
  id: z.string(),
  /**
   * Human-readable description of what this intent represents.
   */
  description: z.string(),
  /**
   * Lowercased keywords/phrases that can be used by orchestrators to
   * recognise this intent in natural-language prompts.
   */
  keywords: z.array(z.string()).default([]),
  /**
   * The MCP tool name that should be preferred when this intent is selected.
   * Example: "calendars.list" or "appointments.book".
   */
  preferredToolName: z.string(),
})

export const WorkplaceAgentDefinitionSchema = z.object({
  workplaceId: z.string(),
  /**
   * Optional app handle or slug associated with this agent definition
   * (for example "acme").
   */
  appHandle: z.string().optional(),
  intents: z.array(WorkplaceIntentDefinitionSchema).default([]),
})

export type WorkplaceIntentDefinition = z.infer<
  typeof WorkplaceIntentDefinitionSchema
>
export type WorkplaceAgentDefinition = z.infer<
  typeof WorkplaceAgentDefinitionSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Multi-stage agent: Persona stage type
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Voice configuration for persona stages.
 * Defines style and formatting rules for message generation.
 */
export const PersonaVoiceSchema = z.object({
  /** Style guidelines as a single string (voice, tone, personality) */
  style: z.string(),
  /** Formatting constraints */
  format: z
    .object({
      /** Maximum character count for messages */
      maxChars: z.number().optional(),
      /** Whether to avoid emojis */
      noEmojis: z.boolean().optional(),
      /** Whether to avoid hyphens */
      noHyphens: z.boolean().optional(),
      /** Whether to avoid bullet points */
      noBulletPoints: z.boolean().optional(),
      /** Maximum questions per message */
      maxQuestionsPerMessage: z.number().optional(),
      /** Whether to avoid sign-offs like "Cheers" or "Thanks" */
      noSignOffs: z.boolean().optional(),
    })
    .optional(),
})

export type PersonaVoice = z.infer<typeof PersonaVoiceSchema>

/**
 * Props for persona stage type.
 * Persona stages transform a message plan into a final message using voice rules.
 * Conversation messages are automatically loaded from workflow context (last 10).
 */
export const PersonaStagePropsSchema = z.object({
  /** Display name for the persona (e.g., "Brett") */
  name: z.string(),
  /** Voice configuration with style and format rules */
  voice: PersonaVoiceSchema,
  /** Input mappings using Liquid templates */
  inputs: z.object({
    /** Message plan from decision stage (Liquid template) */
    plan: z.string(),
    /** Context data from decision stage (Liquid template, optional) */
    data: z.string().optional(),
  }),
  /** LLM model ID override (defaults to gpt-4o-mini) */
  llmModelId: z.string().optional(),
})

export type PersonaStageProps = z.infer<typeof PersonaStagePropsSchema>

/**
 * Persona stage schema for multi-stage agents.
 * Transforms structured message plans into voice-appropriate messages.
 */
export const PersonaStageSchema = z.object({
  /** Unique stage identifier */
  id: z.string(),
  /** Stage type discriminator */
  type: z.literal('persona'),
  /** Dependencies - stages that must complete before this one */
  needs: z.array(z.string()).optional(),
  /** Conditional execution (Liquid template evaluating to boolean) */
  if: z.string().optional(),
  /** Persona stage configuration */
  props: PersonaStagePropsSchema,
})

export type PersonaStage = z.infer<typeof PersonaStageSchema>
